// SignInContacts — "How you sign in": the mobile number and the e-mail address
// of an admin or a doctor, each with its Verified / Not verified pill and a way
// to prove it, in VIEW mode.
//
// WHY (owner, 1 Oct 2026): both profiles drew a red "Not Verified" pill with no
// way to verify the address already on the record — the code flow only lived
// in Edit mode and only when the value was CHANGED. The owner pointed at the
// patient web's "How you sign in" section and asked for exactly that; this is
// its port (VerifiedPill, CredentialOtpPanel, the resend timer and the look),
// with t() keys where the patient web has plain English.
//
// THE SAVE IS THE CALLER'S, AND IT CARRIES ONE FIELD. `onSave` receives
// `{ email, emailProof }` or `{ phone, phoneProof }` and nothing else — never
// the whole form, so verifying an address cannot overwrite a name someone else
// changed in the meantime. The verified flags shown here are the server's
// (`emailVerified`, `phoneNoVerified` on `subject`), re-read after the save by
// the caller's thunk; nothing in this file sets them.
//
// THE PILL FOLLOWS THE TENANT'S POLICY (owner, 1 Oct 2026). Red "Not
// verified" is a call to action, so it is drawn only where the tenant's login
// policy demands a code for that channel (`policy.email.otp` /
// `policy.mobile.otp`); an unverified contact the policy asks no code for gets
// the same words in neutral grey, and a channel with no value gets no pill at
// all. A caller that passes no `policy` keeps the old behaviour (red).
//
// Written once in Frontend-web and copied byte-identical into
// Frontend-superadmin-web (held by Frontend-web/src/test/mirroredFiles.test.js).

import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import {
    CheckCircle2, XCircle, Send, RefreshCw, Loader2, ShieldCheck, Smartphone,
} from 'lucide-react';
import { sendOtp, verifyOtp } from '../../redux/authSlice';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import useSignInContactFlow, { reasonText } from './useSignInContactFlow';

const EMAIL_RE = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const inputCls = (error) =>
    `w-full px-3.5 py-2.5 text-sm border rounded-xl bg-white focus:outline-none focus:ring-2 transition-colors ${error ? 'border-red-300 focus:ring-red-200' : 'border-gray-200 focus:ring-[#4ABDBD]/40 focus:border-[#4ABDBD]'}`;

const linkCls = 'text-[11px] font-semibold text-[#2D8A8A] underline underline-offset-2 hover:text-[#4ABDBD] cursor-pointer';

/* Module-level, not inside the component: declared inside, each render would
   be a new component type and the inputs would lose focus on every keystroke. */

/**
 * @param {boolean} verified
 * @param {boolean} [required=true] the policy demands a code for this channel —
 *        red when unverified; false = the same words in neutral grey.
 */
export function VerifiedPill({ verified, required = true, testId }) {
    const { t } = useTranslation();
    if (verified) {
        return (
            <span data-testid={testId} data-verified="true" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-green-50 text-green-600 border border-green-200">
                <CheckCircle2 size={11} /> {t('signInContacts.verified', 'Verified')}
            </span>
        );
    }
    return required ? (
        <span data-testid={testId} data-verified="false" data-required="true" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-50 text-red-500 border border-red-200">
            <XCircle size={11} /> {t('signInContacts.not_verified', 'Not verified')}
        </span>
    ) : (
        <span data-testid={testId} data-verified="false" data-required="false" className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-gray-50 text-gray-500 border border-gray-200">
            <XCircle size={11} /> {t('signInContacts.not_verified', 'Not verified')}
        </span>
    );
}

/** Does the policy demand a code for this channel? No policy = yes (legacy). */
const channelNeedsCode = (policy, channel) => {
    if (!policy) return true;
    const p = channel === 'phone' ? policy.mobile : policy.email;
    return !!(p && p.otp);
};

/** The panel both contacts render — value step, then code step. */
function ContactOtpPanel({ flow, testId, label, placeholder, inputMode, valueIssue, hint, filter, maxLength }) {
    const { t } = useTranslation();
    if (!flow.open) return null;
    const editable = flow.mode === 'change';
    return (
        <div className="mt-3 p-4 bg-gray-50 rounded-xl border border-gray-100 flex flex-col gap-3" data-testid={testId}>
            {flow.stage === 'value' ? (
                <>
                    <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5">{label}</label>
                        <input
                            data-testid={`${testId}-value`}
                            value={flow.value}
                            readOnly={!editable}
                            onChange={(e) => { if (!filter || filter(e.target.value)) flow.setValue(e.target.value); }}
                            placeholder={placeholder}
                            inputMode={inputMode}
                            maxLength={maxLength}
                            className={`${inputCls(!!(flow.value && valueIssue))} ${editable ? '' : 'bg-gray-100 text-gray-600'}`}
                        />
                        {flow.value && valueIssue ? (
                            <p className="text-xs text-red-500 mt-1">{valueIssue}</p>
                        ) : hint ? (
                            <p className="text-[11px] text-gray-400 mt-1">{hint}</p>
                        ) : null}
                    </div>
                    {flow.error && <p data-testid={`${testId}-error`} className="text-[11px] text-red-600">{flow.error}</p>}
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            data-testid={`${testId}-send`}
                            onClick={() => flow.sendCode()}
                            disabled={flow.busy || !flow.value || !!valueIssue}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-4 py-2 text-xs font-semibold text-white hover:shadow-md disabled:opacity-60 cursor-pointer"
                        >
                            {flow.busy ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                            {t('signInContacts.send_code', 'Send verification code')}
                        </button>
                        <button
                            type="button"
                            data-testid={`${testId}-cancel`}
                            onClick={flow.close}
                            className="text-[11px] text-gray-500 hover:text-gray-700 cursor-pointer"
                        >
                            {t('signInContacts.cancel', 'Cancel')}
                        </button>
                    </div>
                </>
            ) : (
                <>
                    <p className="flex items-center gap-1.5 rounded-xl bg-[#EDF7F7] px-3 py-2 text-[11px] leading-relaxed text-[#2D8A8A]">
                        <ShieldCheck size={13} className="flex-shrink-0" />
                        {t('signInContacts.code_sent_to', { defaultValue: 'Enter the 6-digit code sent to {{value}}', value: flow.value })}
                    </p>
                    <input
                        data-testid={`${testId}-code`}
                        value={flow.code}
                        onChange={(e) => flow.setCode(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); flow.verifyCode(); } }}
                        placeholder={t('signInContacts.code_placeholder', '6-digit code')}
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        className={`${inputCls(!!flow.error)} tracking-widest text-center font-semibold`}
                    />
                    {flow.delivery === 'simulated' && (
                        <p data-testid={`${testId}-dev-hint`} className="text-center text-[11px] text-gray-400">
                            {t('signInContacts.simulated_hint', 'Code delivery is simulated here — use 123456.')}
                        </p>
                    )}
                    {flow.error && <p data-testid={`${testId}-error`} className="text-[11px] text-red-600">{flow.error}</p>}
                    <div className="flex items-center gap-2 flex-wrap">
                        <button
                            type="button"
                            data-testid={`${testId}-verify`}
                            onClick={flow.verifyCode}
                            disabled={flow.busy || flow.code.length !== 6}
                            className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-4 py-2 text-xs font-semibold text-white hover:shadow-md disabled:opacity-60 cursor-pointer"
                        >
                            {flow.busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldCheck size={13} />}
                            {t('signInContacts.verify', 'Verify')}
                        </button>
                        <button
                            type="button"
                            data-testid={`${testId}-resend`}
                            onClick={() => flow.sendCode({ resend: true })}
                            disabled={flow.busy || flow.timer > 0}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#4ABDBD] hover:underline disabled:opacity-50 disabled:no-underline cursor-pointer"
                        >
                            <RefreshCw size={12} />
                            {flow.timer > 0
                                ? t('signInContacts.resend_in', { defaultValue: 'Resend in {{seconds}}s', seconds: flow.timer })
                                : t('signInContacts.resend', 'Resend code')}
                        </button>
                        <button
                            type="button"
                            data-testid={`${testId}-back`}
                            onClick={flow.back}
                            className="ml-auto text-[11px] text-gray-500 hover:text-gray-700 cursor-pointer"
                        >
                            {t('signInContacts.back', 'Back')}
                        </button>
                    </div>
                </>
            )}
        </div>
    );
}

/** One row: label, value, pill, and the Verify / Change / Add links. */
function ContactRow({ label, value, verified, required, emptyText, addText, canWrite, flow, testId, startValue, children }) {
    const { t } = useTranslation();
    return (
        <div>
            <label className="block text-gray-400 font-medium mb-1">{label}</label>
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-gray-800 break-all" data-testid={`${testId}-value`}>{value || emptyText}</p>
                {value && <VerifiedPill verified={verified} required={required} testId={`${testId}-pill`} />}
                {canWrite && !flow.open && (
                    value ? (
                        <>
                            {!verified && (
                                <button type="button" data-testid={`${testId}-verify`} onClick={() => flow.start(startValue(value), 'verify')} className={linkCls}>
                                    {t('signInContacts.verify', 'Verify')}
                                </button>
                            )}
                            <button type="button" data-testid={`${testId}-change`} onClick={() => flow.start(startValue(value), 'change')} className={linkCls}>
                                {t('signInContacts.change', 'Change')}
                            </button>
                        </>
                    ) : (
                        <button type="button" data-testid={`${testId}-add`} onClick={() => flow.start('', 'change')} className={linkCls}>
                            {addText}
                        </button>
                    )
                )}
            </div>
            {children}
        </div>
    );
}

/**
 * @param {object}   props
 * @param {object}   props.subject   the admin/doctor row: { email, phone, emailVerified, phoneNoVerified }
 * @param {Function} props.onSave    ({ email, emailProof } | { phone, phoneProof }) => Promise
 * @param {boolean}  [props.canWrite=true]  false = pills only, no links
 * @param {boolean}  [props.own=true]       the signed-in person's own record (changes the wording)
 * @param {number}   [props.phoneMin=10]
 * @param {number}   [props.phoneMax=10]
 * @param {string}   [props.testIdPrefix='signin']
 * @param {object}   [props.policy]  the tenant's login policy ({ email: { otp }, mobile: { otp } }) —
 *                   decides red vs grey for an unverified contact; omitted = red
 */
export default function SignInContacts({
    subject, onSave, canWrite = true, own = true, phoneMin = 10, phoneMax = 10, testIdPrefix = 'signin', policy,
}) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const axiosInstance = useAxiosAuth();
    const lang = i18n?.language?.startsWith('fr') ? 'fr' : 'en';

    const reason = useCallback((err, step) => reasonText(err, step === 'send'
        ? t('signInContacts.send_failed', 'Could not send the code')
        : step === 'verify'
            ? t('signInContacts.wrong_code', 'That code is not right')
            : t('signInContacts.save_failed', 'Could not save — please try again'), lang), [t, lang]);

    // verify-otp answers { proof: { token, ... } }; a success with no proof
    // would save an address the server then refuses — say so here instead.
    const proofOf = (result) => {
        const token = result?.proof?.token;
        if (!token) throw t('signInContacts.wrong_code', 'That code is not right');
        return token;
    };

    const phoneFlow = useSignInContactFlow({
        send: (value) => dispatch(sendOtp({ phone: value, axiosInstance })).unwrap(),
        verify: async (value, code) => proofOf(await dispatch(verifyOtp({ phone: value, otpCode: code, axiosInstance })).unwrap()),
        save: (value, proof) => onSave({ phone: value, phoneProof: proof }),
        onDone: () => toast.success(t('signInContacts.phone_done', 'Mobile number verified')),
        onLostError: (message) => toast.error(message),
        reason,
    });

    const emailFlow = useSignInContactFlow({
        send: (value) => dispatch(sendOtp({ email: value, axiosInstance })).unwrap(),
        verify: async (value, code) => proofOf(await dispatch(verifyOtp({ email: value, otpCode: code, axiosInstance })).unwrap()),
        save: (value, proof) => onSave({ email: value, emailProof: proof }),
        onDone: () => toast.success(t('signInContacts.email_done', 'E-mail address verified')),
        onLostError: (message) => toast.error(message),
        reason,
    });

    const phoneIssue = phoneFlow.value && !new RegExp(`^\\d{${phoneMin},${phoneMax}}$`).test(phoneFlow.value.trim())
        ? t('signInContacts.phone_invalid', 'Enter a valid mobile number')
        : '';
    const emailIssue = emailFlow.value && !EMAIL_RE.test(emailFlow.value.trim())
        ? t('signInContacts.email_invalid', 'Enter a valid e-mail address')
        : '';

    const p = testIdPrefix;
    return (
        <section data-testid={`${p}-contacts`} className="mt-2 mb-6">
            <h2 className="flex items-center gap-2 text-lg sm:text-xl font-bold mb-4">
                <Smartphone size={18} className="text-[#4ABDBD] shrink-0" />
                <span className="bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                    {own ? t('signInContacts.title_own', 'How you sign in') : t('signInContacts.title_other', 'How they sign in')}
                </span>
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ContactRow
                    label={t('signInContacts.mobile', 'Mobile number')}
                    value={subject?.phone || ''}
                    verified={subject?.phoneNoVerified === true}
                    required={channelNeedsCode(policy, 'phone')}
                    emptyText={t('signInContacts.not_added', 'Not added yet')}
                    addText={t('signInContacts.add_mobile', 'Add a mobile number')}
                    canWrite={canWrite}
                    flow={phoneFlow}
                    testId={`${p}-phone`}
                    startValue={(v) => String(v).replace(/\D/g, '')}
                >
                    <ContactOtpPanel
                        flow={phoneFlow}
                        testId={`${p}-phone-otp`}
                        label={t('signInContacts.mobile', 'Mobile number')}
                        placeholder={t('signInContacts.mobile', 'Mobile number')}
                        inputMode="tel"
                        maxLength={phoneMax}
                        valueIssue={phoneIssue}
                        filter={(v) => /^\d*$/.test(v)}
                        hint={own
                            ? t('signInContacts.phone_hint_own', "We'll text a 6-digit code to this number.")
                            : t('signInContacts.phone_hint_other', 'The code goes to this number — its owner reads it to you.')}
                    />
                </ContactRow>
                <ContactRow
                    label={t('signInContacts.email', 'E-mail address')}
                    value={subject?.email || ''}
                    verified={subject?.emailVerified === true}
                    required={channelNeedsCode(policy, 'email')}
                    emptyText={t('signInContacts.not_added', 'Not added yet')}
                    addText={t('signInContacts.add_email', 'Add an e-mail')}
                    canWrite={canWrite}
                    flow={emailFlow}
                    testId={`${p}-email`}
                    startValue={(v) => String(v)}
                >
                    <ContactOtpPanel
                        flow={emailFlow}
                        testId={`${p}-email-otp`}
                        label={t('signInContacts.email', 'E-mail address')}
                        placeholder="you@example.com"
                        inputMode="email"
                        maxLength={254}
                        valueIssue={emailIssue}
                        hint={own
                            ? t('signInContacts.email_hint_own', "We'll e-mail a 6-digit code to this address.")
                            : t('signInContacts.email_hint_other', 'The code goes to this address — its owner reads it to you.')}
                    />
                </ContactRow>
            </div>
        </section>
    );
}
